import { useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { useAuth, type User } from '../context/AuthContext';
import { Feedback } from '../components/Feedback';
import { PasswordInput } from '../components/PasswordInput';

interface AcceptResponse {
  userId: number;
  orgId: number;
  role: 'admin' | 'member';
  email: string;
  sessionToken: string;
  expiresAt: number;
}

/**
 * Route: `/accept-invite?token=...`. The invite landing page. Validates the
 * token presence, prompts the invitee to set a password, exchanges the
 * token + password for a user account + session via /v1/users/accept-invite,
 * then drops them straight into the dashboard.
 */
export function AcceptInvitePage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const { signupComplete } = useAuth();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!token) {
    return (
      <div>
        <p className="text-muted" style={{ marginBottom: 16 }}>invitation</p>
        <Feedback type="error" message="missing invite token. ask whoever invited you for the link." />
        <p className="text-muted" style={{ marginTop: 16 }}>
          already have an account? <Link to="/login">→ sign in</Link>
        </p>
      </div>
    );
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError('password must be at least 8 characters');
      return;
    }
    if (password !== confirm) {
      setError('passwords do not match');
      return;
    }
    setBusy(true);
    try {
      const res = await apiFetch<AcceptResponse>('/v1/users/accept-invite', {
        method: 'POST',
        body: { inviteToken: token, password },
        skipAuth: true,
      });
      const user: User = {
        id: res.userId,
        email: res.email,
        orgId: res.orgId,
        orgName: '',
        role: res.role,
        schemas: [],
      };
      signupComplete(user, res.sessionToken);
      navigate('/dashboard');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'invite acceptance failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} noValidate>
      <p className="text-muted" style={{ marginBottom: 16 }}>accept invitation</p>
      <p style={{ marginBottom: 16, fontSize: 'var(--font-size-sm)' }}>
        you've been invited to an AstdZ organization. set a password below to activate your account.
      </p>

      {error && <Feedback type="error" message={error} />}

      <div className="form-row">
        <label className="label">password <span className="text-muted">(min 8)</span></label>
        <PasswordInput
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </div>

      <div className="form-row">
        <label className="label">confirm password</label>
        <PasswordInput
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
        />
      </div>

      <button type="submit" className="btn btn-primary" disabled={busy || !password || !confirm}>
        {busy ? 'activating…' : 'activate account'}
      </button>

      <p className="text-muted" style={{ marginTop: 24 }}>
        already have an account? <Link to="/login">→ sign in</Link>
      </p>
    </form>
  );
}
